import { useState, useEffect, useRef, createContext, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Config from "../api/config";
import Http from "../layout/Http";

const ShopAndProductContext = createContext(null);

const useShopAndProductState = () => {
  const { shop_id } = useParams();
  const navigate = useNavigate();
  const [shopInfo, setShopInfo] = useState({
    id: "",
    name: "",
    myshopify_domain: "",
    logo: null,
    socials: [],
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [singleProduct, setSingleProduct] = useState(null);
  const [singleProductLoading, setSingleProductLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchNextCursor, setSearchNextCursor] = useState(null);
  const searchParamsRef = useRef(null);
  // Type of the scanned guest QR code: "product" | "store" | "both".
  const [reviewType, setReviewType] = useState("product");
  // Star icon + corner settings (null = built-in defaults)
  const [globalDisplay, setGlobalDisplay] = useState(null);

  const fetchShopInfo = async () => {
    try {
      const res = await Http.get(
        `${Config.BASE_URL}/customer-management/guest/${shop_id}/shop?guest=true`
      );
      if (res.data?.status && res.data?.shop) {
        setShopInfo(res.data.shop);
        setReviewType(res.data.review_type || "product");
        setGlobalDisplay(res.data.global_display || null);
        return true;
      } else {
        console.warn("Shop info not found");
        return false;
      }
    } catch (err) {
      console.error("Error fetching shop info:", err);
      return false;
    }
  };

  // Every product list response is cursor paginated (5 per page); has_more
  // tells whether next_cursor points to another page.
  const requestProducts = async (params) => {
    const query = new URLSearchParams(params).toString();
    const res = await Http.get(
      `${Config.BASE_URL}/customer-management/guest/${shop_id}/products${
        query ? `?${query}` : ""
      }`
    );
    if (!res.data?.status || !Array.isArray(res.data?.products?.data)) {
      return null;
    }
    const { data, has_more, next_cursor } = res.data.products;
    return { data, nextCursor: has_more ? next_cursor : null };
  };

  const appendUnique = (prev, more) => {
    const existingIds = new Set(prev.map((p) => p.id));
    return [...prev, ...more.filter((p) => !existingIds.has(p.id))];
  };

  const fetchProducts = async () => {
    try {
      const page = await requestProducts({});
      if (page) {
        setProducts(page.data);
        setNextCursor(page.nextCursor);
      } else {
        console.warn("No product data found");
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const fetchMoreProducts = async () => {
    if (!shop_id || !nextCursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const page = await requestProducts({ cursor: nextCursor });
      if (page) {
        setProducts((prev) => appendUnique(prev, page.data));
        setNextCursor(page.nextCursor);
      }
    } catch (err) {
      console.error("Error fetching more products:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const searchProducts = async (
    keyword,
    hasReview = false,
    orderBy = "",
    order = ""
  ) => {
    if (!keyword || !shop_id) return;

    const params = { keyword };
    if (hasReview) params.has_review = "true";
    if (orderBy) params.order_by = orderBy;
    if (order) params.order = order;
    // Later pages must resend the same params; also lets stale responses be dropped.
    searchParamsRef.current = params;

    setSearchLoading(true);
    setSearchNextCursor(null);
    try {
      const page = await requestProducts(params);
      if (searchParamsRef.current !== params) return [];

      setSearchResults(page?.data ?? []);
      setSearchNextCursor(page?.nextCursor ?? null);
      return page?.data ?? [];
    } catch (err) {
      if (searchParamsRef.current === params) setSearchResults([]);
      return [];
    } finally {
      if (searchParamsRef.current === params) setSearchLoading(false);
    }
  };

  const fetchMoreSearchResults = async () => {
    const params = searchParamsRef.current;
    if (!shop_id || !params || !searchNextCursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const page = await requestProducts({ ...params, cursor: searchNextCursor });
      if (page && searchParamsRef.current === params) {
        setSearchResults((prev) => appendUnique(prev, page.data));
        setSearchNextCursor(page.nextCursor);
      }
    } catch (err) {
      console.error("Error fetching more search results:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const clearSearchResults = () => {
    searchParamsRef.current = null;
    setSearchResults([]);
    setSearchNextCursor(null);
    setSearchLoading(false);
  };

  const fetchSingleProduct = async (productId) => {
    if (!productId || !shop_id) return null;

    setSingleProductLoading(true);
    try {
      const existingProduct = products.find((p) => p.id === Number(productId));
      if (existingProduct) {
        setSingleProduct(existingProduct);
        return existingProduct;
      }

      const res = await Http.get(
        `${Config.BASE_URL}/customer-management/guest/${shop_id}/products?product_id=${productId}`
      );

      if (res.data?.status && Array.isArray(res.data?.products?.data)) {
        const allProducts = res.data.products.data;
        const product = allProducts.find((p) => p.id === Number(productId));

        if (product) {
          setSingleProduct(product);
          setProducts((prev) => {
            const exists = prev.find((p) => p.id === product.id);
            if (!exists) {
              return [...prev, product];
            }
            return prev;
          });
          return product;
        } else {
          console.warn(
            `Product with ID ${productId} not found in expanded list`
          );
        }
      }

      setSingleProduct(null);
      return null;
    } catch (err) {
      setSingleProduct(null);
      return null;
    } finally {
      setSingleProductLoading(false);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      if (!shop_id) {
        navigate("/404");
        return;
      }
      setLoading(true);
      const [shopExists] = await Promise.all([
        fetchShopInfo(),
        fetchProducts(),
      ]);
      if (!shopExists) {
        navigate("/404");
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  return {
    shopInfo,
    reviewType,
    globalDisplay,
    products,
    loading,
    searchProducts,
    searchResults,
    searchLoading,
    clearSearchResults,
    fetchSingleProduct,
    singleProduct,
    singleProductLoading,
    fetchMoreProducts,
    loadingMore,
    hasMoreProducts: !!nextCursor,
    fetchMoreSearchResults,
    hasMoreSearchResults: !!searchNextCursor,
  };
};

export const ShopAndProductProvider = ({ children }) => {
  const value = useShopAndProductState();
  return (
    <ShopAndProductContext.Provider value={value}>
      {children}
    </ShopAndProductContext.Provider>
  );
};

const useShopAndProductInfo = () => {
  const context = useContext(ShopAndProductContext);
  if (!context) {
    throw new Error(
      "useShopAndProductInfo must be used within a ShopAndProductProvider"
    );
  }
  return context;
};

export default useShopAndProductInfo;
